import React, { useEffect, useMemo, useState } from 'react';
import { pr as api } from '../services/api';
import { nb, eurCourt, pct1, signePct, CelluleDelta, useFiltresVentes, PanneauVentes, usePanneau, useCanal, basculer } from './ventesUtils';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './VentesJour.css';  // rg-* du regroupement
import './Ventes.css';
import './Marche.css';   // bouton « Analyse IA » (mk-ia, mk-tete-d)
import PrAnalyseIa from './PrAnalyseIa';
import { useBi } from '../BiContext';

// ─── PR — Performance ────────────────────────────────────────────────────────
// Même ossature que VN/VO et APV : bandeau vs N-1, bloc par société à trois
// mailles, bloc d'analyse à quatre axes, détail récent. Ce qui lui est propre :
// le CANAL, comptoir ou atelier, qui est aussi le premier axe d'analyse.

const nfr = new Intl.NumberFormat('fr-FR');
const eur = (v) => (v == null ? '—' : `${nfr.format(Math.round(v))} €`);

const AXES = [
  { c: 'marque', lib: 'marque', col: 'Marque' },
  { c: 'atelier', lib: 'atelier', col: 'Atelier' },
  { c: 'receptionnaire', lib: 'vendeur', col: 'Vendeur' },
];

export default function PrPerformance({ branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [axe, setAxe] = useState('marque');
  // Analyse IA (2026-10-02) : Auto Antilles seulement, sur la période du rapport.
  const [ia, setIa] = useState(false);
  const { ia: droitIa } = useBi(); // droit « Analyse IA » de la personne connectée
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  // Le canal est un périmètre, pas un réglage d'affichage : il vit dans le
  // panneau et recale tout l'écran (voir `useCanal`).
  const { canal, choix } = useCanal(f);

  useEffect(() => {
    let actif = true; setErreur('');
    api.performance({ periode: f.periode, mois: f.moisStr, societes: f.societesUrl, canal, branche })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [branche, f.periode, f.moisStr, f.societesUrl, canal]);

  const feuilles = useMemo(
    () => (data?.parSociete || []).map((s) => ({ ...s, libelle: s.nom, code: s.code })), [data]);
  const axesDispo = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  // ⚠ Les KPI d'un regroupement se RECALCULENT : additionner des taux de marge
  // ou des paniers moyens n'a aucun sens.
  const sommer = (membres) => {
    const som = (g) => membres.reduce((t, m) => t + (g(m) || 0), 0);
    const ca = som((m) => m.ca.valeur), caN1 = som((m) => m.ca.n1);
    const mg = som((m) => m.marge.valeur), mgN1 = som((m) => m.marge.n1);
    const nf = som((m) => m.factures.valeur), nfN1 = som((m) => m.factures.n1);
    const d = (a, b) => (b ? ((a - b) / Math.abs(b)) * 100 : null);
    const tx = ca ? (mg / ca) * 100 : null, txN1 = caN1 ? (mgN1 / caN1) * 100 : null;
    return {
      ca: { valeur: ca, n1: caN1, deltaPct: d(ca, caN1) },
      marge: { valeur: mg, n1: mgN1, deltaPct: d(mg, mgN1) },
      taux: { valeur: tx, n1: txN1, deltaPt: (tx != null && txN1 != null) ? tx - txN1 : null },
      factures: { valeur: nf, n1: nfN1, deltaPct: d(nf, nfN1) },
      panier: { valeur: nf ? ca / nf : null, n1: nfN1 ? caN1 / nfN1 : null,
        deltaPct: d(nf ? ca / nf : 0, nfN1 ? caN1 / nfN1 : 0) },
    };
  };
  const maille = useRegroupement();
  const arbre = useMemo(
    () => construireArbre(feuilles, maille.niveau, sommer, maille.ouverts, (x) => x.ca?.valeur),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);

  const Panneau = (
    <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo}
      extra={{ choix }} />
  );
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const k = data.kpis;
  const AXE = AXES.find((a) => a.c === axe) || AXES[0];
  const lignesAxe = (data.parAxe || {})[axe] || [];

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>PR — Performance</h1></div>
          <div className="mk-tete-d">
            {branche === 'auto-antilles' && droitIa !== 'aucune' && (
              <button type="button" className="mk-ia" onClick={() => setIa(true)}
                title="Analyse IA des pièces : taux de marge par concession, canal et type d'OR, pertes, pistes d'action — concessions pseudonymisées">Analyse IA</button>
            )}
            <div className="vt-asof"><b>{data.periode}</b>{canal && <span> · {canal.toLowerCase()}</span>}</div>
          </div>
        </div>

        {canal && (
          <div className="vj-sel">
            <span>Périmètre limité au <b>{canal.toLowerCase()}</b> — tous les blocs le suivent.</span>
            <button type="button" className="vj-sel-x" onClick={() => f.setFiltre({ canal: '' })}>Voir les deux canaux</button>
          </div>
        )}

        <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
          <div className="vt-kpi"><div className="l">CA pièces HT</div><div className="v">{eurCourt(k.ca.valeur)}</div>
            <div className={`d ${(k.ca.deltaPct || 0) >= 0 ? 'pos' : 'neg'}`}>{signePct(k.ca.deltaPct)}<small> vs N-1</small></div></div>
          <div className="vt-kpi"><div className="l">Marge</div><div className="v">{eurCourt(k.marge.valeur)}</div>
            <div className={`d ${(k.marge.deltaPct || 0) >= 0 ? 'pos' : 'neg'}`}>{signePct(k.marge.deltaPct)}<small> vs N-1</small></div></div>
          <div className="vt-kpi"><div className="l">Taux de marge</div><div className="v">{pct1(k.taux.valeur)}</div>
            <div className={`d ${(k.taux.deltaPt || 0) >= 0 ? 'pos' : 'neg'}`}>{k.taux.deltaPt == null ? '—' : `${k.taux.deltaPt > 0 ? '+' : ''}${k.taux.deltaPt} pt`}<small> vs N-1</small></div></div>
          <div className="vt-kpi"><div className="l">Factures</div><div className="v">{nb(k.factures.valeur)}</div>
            <div className={`d ${(k.factures.deltaPct || 0) >= 0 ? 'pos' : 'neg'}`}>{signePct(k.factures.deltaPct)}<small> vs N-1</small></div></div>
          <div className="vt-kpi"><div className="l">Panier moyen</div><div className="v">{eur(k.panier.valeur)}</div>
            <div className={`d ${(k.panier.deltaPct || 0) >= 0 ? 'pos' : 'neg'}`}>{signePct(k.panier.deltaPct)}<small> vs N-1</small></div></div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Par société</h2>
            <div className="st-tools">
              <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axesDispo} />
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl">
              <thead><tr>
                <th className="l">Société</th>
                <th>CA HT</th><th className="vt-dh">vs N-1</th>
                <th>Marge</th><th className="vt-dh">vs N-1</th>
                <th>Taux</th><th className="vt-dh">vs N-1</th>
                <th>Factures</th><th>Panier moyen</th>
              </tr></thead>
              <tbody>
                {arbre.map((s) => {
                  const cible = s.feuille ? s.code : (s.membres || []).join(',');
                  return (
                    <tr key={s.cle} className={`tbl-clic rg-n${s.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
                      onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
                      <td className="l">
                        <span className="rg-in" style={{ width: s.niveau * 14 }} />
                        {s.feuille ? <span className="rg-chev vide" /> : (
                          <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`}
                            title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par société'}
                            aria-label="Déplier"
                            onClick={(e) => { e.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
                        )}
                        {s.libelle || s.nom}
                        {!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
                      </td>
                      <td>{eurCourt(s.ca.valeur)}</td><CelluleDelta valeur={s.ca.valeur} n1={s.ca.n1} fmt={eurCourt} />
                      <td>{eurCourt(s.marge.valeur)}</td><CelluleDelta valeur={s.marge.valeur} n1={s.marge.n1} fmt={eurCourt} />
                      <td className="vt-taux">{pct1(s.taux.valeur)}</td><CelluleDelta pt={s.taux.deltaPt} />
                      <td>{nb(s.factures.valeur)}</td>
                      <td>{eur(s.panier.valeur)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr>
                <td className="l">Total</td>
                <td>{eurCourt(k.ca.valeur)}</td><CelluleDelta valeur={k.ca.valeur} n1={k.ca.n1} fmt={eurCourt} />
                <td>{eurCourt(k.marge.valeur)}</td><CelluleDelta valeur={k.marge.valeur} n1={k.marge.n1} fmt={eurCourt} />
                <td>{pct1(k.taux.valeur)}</td><CelluleDelta pt={k.taux.deltaPt} />
                <td>{nb(k.factures.valeur)}</td><td>{eur(k.panier.valeur)}</td>
              </tr></tfoot>
            </table>
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Analyse par {AXE.lib}</h2>
            <div className="st-tools">
              <div className="st-seg">
                {AXES.map((a) => {
                  const off = !((data.parAxe || {})[a.c] || []).length;
                  return (
                    <button key={a.c} type="button" disabled={off}
                      className={axe === a.c ? 'on' : ''}
                      title={off ? `« ${a.col} » n'est renseigné sur aucune facture de la période` : ''}
                      onClick={() => setAxe(a.c)}>{a.lib}</button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap vt-borne">
            <table className="vt-tbl">
              <thead><tr><th className="l">{AXE.col}</th><th>CA HT</th><th>Marge</th><th>Taux</th><th>Factures</th><th>Panier moyen</th></tr></thead>
              <tbody>
                {lignesAxe.map((x) => (
                  <tr key={x.nom}>
                    <td className="l">{x.nom}</td>
                    <td>{eurCourt(x.ca)}</td>
                    <td>{eurCourt(x.marge)}</td>
                    <td className="vt-taux">{pct1(x.taux)}</td>
                    <td>{nb(x.factures)}</td>
                    <td>{eur(x.panier)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td className="l">Total</td>
                <td>{eurCourt(k.ca.valeur)}</td><td>{eurCourt(k.marge.valeur)}</td>
                <td>{pct1(k.taux.valeur)}</td><td>{nb(k.factures.valeur)}</td><td>{eur(k.panier.valeur)}</td>
              </tr></tfoot>
            </table>
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>Détail des factures récentes</h2></div>
          <div className="vt-card-b vt-tbl-wrap vt-borne">
            <table className="vt-tbl">
              <thead><tr><th className="l">Date</th><th className="l">Facture</th><th className="l">Canal</th><th className="l">Marque</th><th className="l">Vendeur</th><th>CA HT</th><th>Marge</th><th>Taux</th></tr></thead>
              <tbody>
                {(data.detail || []).map((d, i) => (
                  <tr key={i} className={d.marge < 0 ? 'vt-neg' : ''}>
                    <td className="l">{d.date ? d.date.split('-').reverse().join('/') : '—'}</td>
                    <td className="l">{d.facture}</td>
                    <td className="l">{d.canal}</td>
                    <td className="l">{d.marque}</td>
                    <td className="l">{d.vendeur}</td>
                    <td>{eur(d.ca)}</td>
                    <td>{eur(d.marge)}</td>
                    <td>{d.taux == null ? '—' : pct1(d.taux)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      {Panneau}
      {ia && <PrAnalyseIa selection={f.selected} filtres={{ periode: f.periode, mois: f.moisStr }} libellePeriode={data.periode} onFermer={() => setIa(false)} />}
    </>
  );
}
